import { Navigate, Route, Routes } from "react-router-dom";
import React, { Suspense } from "react";
import NotFound from "js/pages/NotFound";
import ContractDetails from "js/components/Contracts/ContractDetails";
import Success from "js/pages/Contracts/Success";
import ContractsDashboard from "js/pages/Contracts/ContractsDashboard";
import { Spin } from "antd";

import { lazyWithReload } from "utils/lazyWithReload";

// INVARIANTE: en este repo NINGUN `import()` dinamico va a pelo. Ni con `React.lazy`, ni
// con `const mod = await import(...)`: los dos producen el mismo chunk y el mismo fallo.
// Una pestaña abierta durante un deploy pide un asset con hash que ya no existe, el
// `import()` se rechaza ("Unable to preload CSS for ...") y el error sube al
// `Sentry.ErrorBoundary` de `App.tsx` (MB-APP-EXPRESS-167): issue en Sentry y modal de
// feedback al usuario, sin ninguna forma de recuperarse en ese documento. Y un `import()`
// suelto en un handler async es PEOR: sin Suspense ni boundary en ese camino acaba en
// `onunhandledrejection` y el usuario solo ve un boton muerto.
// Lo vigilan los dos barridos de `src/tests/utils/lazyWithReload.test.tsx` sobre todo
// `src/`: cero referencias a `lazy` de React y cero `import()` fuera de los argumentos de
// `lazyWithReload`. Si hace falta cargar un modulo que NO es un componente, no basta con
// añadirlo al allowlist: hay que envolverlo en un helper que recupere igual.
const ContractEditorPage = lazyWithReload(() => import("js/pages/Contracts/ContractEditorPage"), {
  // El enlace de salida lo declara la ruta, no el helper (ver LazyWithReloadOptions).
  backHref: "/contracts",
  backLabel: "Volver a contratos",
});

const EditorFallback = (
  <Spin size="large" className="flex justify-center items-center h-screen" />
);

export const ContractEditor: React.FC = () => (
  <Suspense fallback={EditorFallback}>
    <ContractEditorPage />
  </Suspense>
);

// El editor ocupa toda la ventana, así que sus rutas no viven aquí, dentro del tablero (menú y
// encabezado), sino en `App.tsx` bajo `FullScreenLayout`. Para salir trae su propia flecha
// ("Volver a contratos").
export const CONTRACT_EDITOR_PATHS = [
  "/contracts/create",
  "/contracts/:contractIdParam/edit",
  "/contracts/:folderIdParam/create",
];

const Contracts: React.FC = () => {
  return (
    <Routes>
      <Route path="/new" element={<Navigate to={"/contracts/create"} />} />
      <Route path="/" element={<ContractsDashboard />} />
      <Route path="/:contractId/details" element={<ContractDetails />} />
      <Route path="/success" element={<Success />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default Contracts;
